(function (root) {
  'use strict';

  const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const normalize = value => String(value || '').toLowerCase().trim();
  // Framing measured on the actual 1040 × 1400 catalog photos; packaging stays intact.
  const productCrop = product => {
    const frames = {
      'MBP8103-Modifica-2_idra-prot_1.jpg': 'cream',
      'MBP8099-Modifica-2_pelli_sens_1.jpg': 'cream',
      'MBP8101-Modifica-2_sebo_1.jpg': 'cream',
      'MBP8105-Modifica_giorno_anti-age_1.jpg': 'cream',
      'MBP8090-Modifica_viso_1.jpg': 'serum',
      'MBP8097-Modifica-2_contorno_1.jpg': 'serum'
    };
    const filename = String(product?.image || '').split('?')[0].split('/').pop();
    return frames[filename] || 'full';
  };
  const productImage = (src, alt, crop) => `<span class="skin-quiz__product-frame skin-quiz__product-frame--${crop}"><img src="${escapeHtml(src || '')}" alt="${escapeHtml(alt)}" width="1040" height="1400" loading="eager"></span>`;

  // Compatibility is checked against the actual product metafield, not its name.
  function compatible(product, profile) {
    if (!product || !Array.isArray(product.skinTypes)) return false;
    const types = product.skinTypes.map(normalize);
    return types.includes('tutti i tipi di pelle') || (profile.skinTypes || []).some(type => types.includes(normalize(type)));
  }

  function recommend(data, answers) {
    const profile = data.profiles.find(item => item.key === answers.skin);
    if (!profile) return [];
    const items = [];
    function add(product, reason, role) {
      if (!product || !compatible(product, profile) || !product.variantId || items.some(item => item.product.variantId === product.variantId)) return;
      items.push({ product, reason, role });
    }
    if (answers.cleanse === 'add') add(profile.cleanser, profile.cleanserReason, 'Detersione');
    if (answers.serum === 'yes') add(data.serum, 'Hai scelto di includere un siero viso. Mamata lo descrive per idratazione e perdita di elasticità.', 'Siero viso');
    if (answers.eye === 'yes') add(data.eye, 'Hai scelto un trattamento per il contorno occhi. La scheda Mamata descrive una texture leggera con acido ialuronico e olio di Argan.', 'Contorno occhi');
    add(profile.moisturizer, profile.moisturizerReason, profile.key === 'matura' ? 'Crema giorno' : 'Crema viso');
    return items;
  }

  const sum = items => items.reduce((total, item) => total + item.product.price, 0);
  root.MamataQuizLogic = { recommend, compatible, sum };
  if (typeof module !== 'undefined') module.exports = root.MamataQuizLogic;
  if (!root.customElements || root.customElements.get('skin-quiz')) return;

  class SkinQuiz extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try { this.data = JSON.parse(this.querySelector('[data-skin-quiz-data]').textContent); }
      catch { return; }
      this.answers = {};
      this.index = 0;
      this.items = [];
      this.selected = new Set();
      this.cartInFlight = false;
      this.cartNeedsRefresh = false;
      this.transitionTimer = null;
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onPageShow = event => { if (event.persisted) this.resetCartState(); };
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      root.addEventListener('pageshow', this.onPageShow);
      this.q('[data-action="start"]').disabled = !this.data.profiles.length;
    }

    disconnectedCallback() {
      clearTimeout(this.transitionTimer);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      root.removeEventListener('pageshow', this.onPageShow);
      this.ready = false;
    }

    q(selector) { return this.querySelector(selector); }
    money(cents) { return new Intl.NumberFormat(this.data.locale || 'it-IT', { style: 'currency', currency: this.data.currency || 'EUR' }).format(cents / 100); }

    questions() {
      const profile = this.data.profiles.find(item => item.key === this.answers.skin) || this.data.profiles[0];
      const list = [{
        id: 'skin', title: 'Quale descrizione senti più vicina alla tua pelle?',
        hint: 'Se ti riconosci in più risposte, scegli l’esigenza che vuoi considerare per prima.',
        note: 'Scegli in base a come senti la pelle.',
        aiNote: 'Dettagli illustrativi creati con AI',
        options: this.data.profiles.map(item => ({ value: item.key, title: item.label, text: item.hint, image: item.image, skin: true }))
      }];
      if (this.data.serum) list.push({
        id: 'serum', title: 'Come preferisci comporre la tua routine viso?',
        hint: 'Partiamo dalla crema scelta per la tua esigenza. Il siero si aggiunge solo se lo vuoi.',
        options: [
          { value: 'no', title: 'Essenziale, con la crema', text: 'Preferisco scegliere una crema viso.', image: profile.moisturizer?.image, crop: productCrop(profile.moisturizer) },
          { value: 'yes', title: 'Vorrei anche un siero viso', text: 'Valuto un siero da affiancare alla crema.', image: this.data.serum.image, crop: productCrop(this.data.serum) }
        ]
      });
      if (this.data.eye) list.push({
        id: 'eye', title: 'Ti serve anche un trattamento per il contorno occhi?',
        hint: 'Il contorno occhi è una scelta separata dal siero viso.',
        options: [
          { value: 'no', title: 'Per ora no', text: 'Ne uso già uno, oppure non desidero aggiungerlo.', icon: 'keep' },
          { value: 'yes', title: 'Sì, vorrei valutarlo', text: 'Mostrami il siero contorno occhi Mamata.', image: this.data.eye.image, crop: productCrop(this.data.eye) }
        ]
      });
      list.push({
        id: 'cleanse', title: 'Per la detersione, da cosa parti?',
        hint: 'Se vuoi continuare a usare il tuo detergente, lo teniamo fuori dalla selezione.',
        options: [
          { value: 'keep', title: 'Tengo il detergente che uso', text: 'Non voglio acquistarne un altro.', icon: 'keep' },
          { value: 'add', title: 'Vorrei scegliere un detergente', text: 'Non ne ho uno, oppure desidero cambiarlo.', image: profile.cleanser?.image, crop: productCrop(profile.cleanser) }
        ]
      });
      return list;
    }

    onClick(event) {
      const button = event.target.closest('[data-action]');
      if (!button || button.disabled || this.cartInFlight) return;
      const action = button.dataset.action;
      if (action === 'start') { this.index = 0; this.show('questions'); this.renderQuestion(); }
      if (action === 'back') {
        clearTimeout(this.transitionTimer);
        if (this.index > 0) { this.index--; this.renderQuestion(); } else this.show('intro');
      }
      if (action === 'next') this.next();
      if (action === 'restart') {
        clearTimeout(this.transitionTimer);
        this.index = 0; this.show('questions'); this.renderQuestion();
      }
      if (action === 'cart') this.addToCart();
    }

    onChange(event) {
      const input = event.target;
      if (input.dataset.question) {
        clearTimeout(this.transitionTimer);
        this.answers[input.dataset.question] = input.value;
        this.q('[data-action="next"]').disabled = false;
      }
      if (input.dataset.selectProduct) {
        const id = Number(input.dataset.selectProduct);
        input.checked ? this.selected.add(id) : this.selected.delete(id);
        this.renderTotal();
      }
    }

    next() {
      const question = this.questions()[this.index];
      if (!this.answers[question.id]) return;
      if (this.index < this.questions().length - 1) { this.index++; this.renderQuestion(); }
      else this.renderResult();
    }

    show(name) {
      this.querySelectorAll('[data-screen]').forEach(panel => { panel.hidden = panel.dataset.screen !== name; });
    }

    renderQuestion() {
      const questions = this.questions();
      const question = questions[this.index];
      const position = this.index + 1;
      this.q('[data-progress-label]').textContent = `Domanda ${position} di ${questions.length}`;
      this.q('[data-progress-bar]').style.width = `${position / questions.length * 100}%`;
      let html = `<fieldset class="skin-quiz__q"><legend class="skin-quiz__q-title h3" tabindex="-1">${escapeHtml(question.title)}</legend>`;
      html += `<p class="skin-quiz__q-hint">${escapeHtml(question.hint)}</p>`;
      html += `<ul class="skin-quiz__options ${question.id === 'skin' ? 'skin-quiz__options--skin' : 'skin-quiz__options--two'}">`;
      question.options.forEach((option, index) => {
        const id = `${this.id}-${question.id}-${index}`;
        html += `<li class="skin-quiz__option"><input type="radio" name="${this.id}-answer" id="${id}" data-question="${question.id}" value="${escapeHtml(option.value)}" ${this.answers[question.id] === option.value ? 'checked' : ''}>`;
        html += `<label class="skin-quiz__option-face" for="${id}">`;
        if (option.image) html += option.skin
          ? `<span class="skin-quiz__option-media is-skin"><img src="${escapeHtml(option.image)}" alt="" width="480" height="360" loading="eager"></span>`
          : `<span class="skin-quiz__option-media is-product">${productImage(option.image, '', option.crop || 'full')}</span>`;
        else html += '<span class="skin-quiz__option-media is-symbol" aria-hidden="true"><svg viewBox="0 0 80 80" fill="none"><circle cx="40" cy="40" r="28" stroke="currentColor" stroke-width="1.5"/><path d="m27 40 9 9 18-19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
        html += `<span class="skin-quiz__option-copy"><span class="skin-quiz__option-main">${escapeHtml(option.title)}</span><span class="skin-quiz__option-sub">${escapeHtml(option.text)}</span></span></label></li>`;
      });
      html += '</ul></fieldset>';
      if (question.note) html += `<p class="skin-quiz__choice-instruction">${escapeHtml(question.note)}</p>`;
      if (question.aiNote) html += `<p class="skin-quiz__image-note">${escapeHtml(question.aiNote)}</p>`;
      this.q('[data-stage]').innerHTML = html;
      const next = this.q('[data-action="next"]');
      next.disabled = !this.answers[question.id];
      next.textContent = position === questions.length ? 'Vedi la tua selezione' : 'Continua';
      this.q('.skin-quiz__q-title').focus({ preventScroll: true });
      this.scrollIntoView({ behavior: root.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    }

    renderResult() {
      const profile = this.data.profiles.find(item => item.key === this.answers.skin);
      this.items = recommend(this.data, this.answers);
      this.selected = new Set(this.items.filter(item => item.product.available).map(item => item.product.variantId));
      this.q('[data-result-title]').textContent = 'La selezione per le tue risposte';
      this.q('[data-result-description]').textContent = `${profile.name}: ${profile.summary} Puoi togliere qualsiasi prodotto prima di aggiungere la selezione al carrello.`;
      this.q('[data-result-notes]').textContent = this.answers.cleanse === 'keep'
        ? 'Hai scelto di mantenere il tuo detergente: non ne abbiamo aggiunto un altro.'
        : 'La selezione include il detergente indicato qui sotto. Segui le modalità d’uso riportate nelle schede dei prodotti.';
      this.q('[data-products]').innerHTML = this.items.map(({ product, reason, role }) => {
        const id = `${this.id}-product-${product.variantId}`;
        return `<article class="skin-quiz__product"><a class="skin-quiz__product-image" href="${escapeHtml(product.url)}">${productImage(product.image, product.title, productCrop(product))}</a><div class="skin-quiz__product-body"><p class="skin-quiz__product-step">${escapeHtml(role)}</p><h3 class="h5"><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></h3><p class="skin-quiz__reason">${escapeHtml(reason)}</p><p class="skin-quiz__product-price">${this.money(product.price)}</p><a class="skin-quiz__details" href="${escapeHtml(product.url)}">Ingredienti e modalità d’uso</a><label class="skin-quiz__select" for="${id}"><input id="${id}" type="checkbox" data-select-product="${product.variantId}" ${product.available ? 'checked' : 'disabled'}> ${product.available ? 'Includi nella selezione' : 'Al momento non disponibile'}</label></div></article>`;
      }).join('');
      this.show('result');
      this.renderTotal();
      this.q('[data-result-title]').focus({ preventScroll: true });
      this.scrollIntoView({ behavior: root.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    }

    chosenItems() { return this.items.filter(item => this.selected.has(item.product.variantId) && item.product.available); }

    renderTotal() {
      const items = this.chosenItems();
      this.q('[data-total]').textContent = this.money(sum(items));
      this.q('[data-count]').textContent = `${items.length} ${items.length === 1 ? 'prodotto selezionato' : 'prodotti selezionati'}`;
      this.q('[data-action="cart"]').disabled = this.cartInFlight || (!this.cartNeedsRefresh && !items.length);
      this.q('[data-cart-error]').textContent = '';
    }

    resetCartState() {
      if (!this.cartInFlight) return;
      this.cartInFlight = false;
      (this.cartControls || []).forEach(control => { control.disabled = false; });
      const button = this.q('[data-action="cart"]');
      button.textContent = this.cartButtonLabel;
      button.removeAttribute('aria-busy');
      this.renderTotal();
    }

    async addToCart() {
      const items = this.chosenItems();
      if ((!items.length && !this.cartNeedsRefresh) || this.cartInFlight) return;
      const button = this.q('[data-action="cart"]');
      const drawer = document.querySelector('cart-drawer');
      const theme = root.FoxTheme;
      if (!drawer || typeof drawer.show !== 'function' || !theme?.pubsub) {
        this.q('[data-cart-error]').textContent = 'Il carrello laterale non è disponibile. Ricarica la pagina e riprova.';
        return;
      }
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
      const sections = [...new Set(sectionsToBundle)].slice(0, 5);
      const drawerSection = theme.utils.getSectionId(drawer);
      const label = 'Aggiungi la selezione al carrello';
      const controls = [...this.querySelectorAll('[data-select-product], [data-action="restart"]')].filter(control => !control.disabled);
      this.cartButtonLabel = label;
      this.cartControls = controls;
      this.cartInFlight = true;
      button.disabled = true;
      button.textContent = this.cartNeedsRefresh ? 'Apertura del carrello…' : 'Aggiunta in corso…';
      button.setAttribute('aria-busy', 'true');
      controls.forEach(control => { control.disabled = true; });
      this.q('[data-cart-error]').textContent = '';
      try {
        if (!this.cartNeedsRefresh) {
          const response = await fetch(`${this.data.root}cart/add.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: items.map(item => ({ id: item.product.variantId, quantity: 1 })), sections, sections_url: root.location.pathname })
          });
          const result = await response.json().catch(() => null);
          if (!response.ok) throw new Error(result?.description || result?.message || `Non siamo riusciti ad aggiornare il carrello (errore ${response.status}). Riprova tra poco.`);
          if (!Array.isArray(result?.items) || !result.items.length) throw new Error('Il negozio non ha confermato l’aggiunta. Controlla il carrello prima di riprovare.');
          this.cartNeedsRefresh = true;
          this.pendingCartSections = result.sections;
        }
        button.textContent = 'Apertura del carrello…';
        const cartResponse = await fetch(`${this.data.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        const cart = await cartResponse.json();
        if (!cartResponse.ok || !Array.isArray(cart.items)) throw new Error('Carrello non disponibile');
        if (typeof this.pendingCartSections?.[drawerSection] !== 'string') {
          const renderedResponse = await fetch(`${root.location.pathname}?sections=${encodeURIComponent(sections.join(','))}`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (!renderedResponse.ok) throw new Error('Carrello laterale non disponibile');
          this.pendingCartSections = await renderedResponse.json();
        }
        if (typeof this.pendingCartSections?.[drawerSection] !== 'string') throw new Error('Carrello laterale non disponibile');
        cart.sections = this.pendingCartSections;
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { sources: 'skin-quiz', cart });
        this.resetCartState();
        drawer.show(button);
        this.cartNeedsRefresh = false;
        this.pendingCartSections = null;
        this.renderTotal();
      } catch (error) {
        this.resetCartState();
        if (this.cartNeedsRefresh) {
          button.textContent = 'Apri il carrello';
          button.disabled = false;
          this.q('[data-cart-error]').textContent = 'I prodotti sono stati aggiunti, ma il carrello laterale non si è aggiornato. Premi “Apri il carrello” per riprovare ad aprirlo: i prodotti non verranno aggiunti di nuovo.';
        } else {
          this.q('[data-cart-error]').textContent = error instanceof TypeError ? 'La connessione si è interrotta. Controlla il carrello prima di riprovare.' : error.message;
        }
      }
    }
  }
  root.customElements.define('skin-quiz', SkinQuiz);
})(typeof window !== 'undefined' ? window : globalThis);
